import React from "react";
import { Link } from "react-router-dom";

// =====================================================================================
// IMPORTANT: LEGAL PLACEHOLDER CONTENT
// This entire page contains placeholder text for the Privacy Policy.
// It MUST be replaced with your actual, legally reviewed Privacy Policy.
// Consult with a legal professional to ensure compliance.
// =====================================================================================

const PrivacyPolicyPage = () => {
  const effectiveDate = "13th June 2025";
  const privacyContactEmail = "support@practap.com";


  return (
    <main className="min-h-screen bg-slate-900 text-slate-200 py-12 px-4 sm:px-6 lg:px-8"> {/* Base text color improved */}
      <article className="max-w-3xl mx-auto bg-slate-800 p-6 sm:p-8 shadow-xl rounded-lg"> {/* article tag, adjusted padding */}
        <nav aria-label="Breadcrumb" className="mb-6"> {/* Added nav for breadcrumb-like link */}
          <Link
            to="/"
            className="text-sky-400 hover:text-sky-300 inline-block group" /* Contrast Fix */
          >
            <span aria-hidden="true" className="transition-transform duration-200 ease-in-out group-hover:-translate-x-1 inline-block">←</span> Back to Home
          </Link>
        </nav>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-50 mb-8 text-center"> {/* Contrast Fix */}
          Privacy Policy for Practap
        </h1>
        <p className="mb-6 text-sm text-slate-300 text-center"> {/* Contrast Fix */}
          Effective Date: {effectiveDate}
        </p>

        {/* ===================================================================================== */}
        {/* BEGIN ACTUAL PRIVACY POLICY CONTENT (CURRENTLY PLACEHOLDER)                       */}
        {/* Replace all content within this div with your finalized Privacy Policy text.         */}
        {/* ===================================================================================== */}
        <div className="space-y-6 prose prose-slate prose-invert max-w-none
                        prose-headings:text-slate-100 prose-p:text-slate-200
                        prose-ul:text-slate-200 prose-li:marker:text-sky-400
                        prose-a:text-sky-400 hover:prose-a:text-sky-300"> {/* Tailwind typography for readability */}
          <p>
            At Practap, your privacy is important to us. This Privacy Policy
            explains how we handle your personal information when you use our
            platform.
          </p>

          <section aria-labelledby="info-we-collect">
            <h2 id="info-we-collect" className="text-2xl font-semibold text-slate-100 mb-3">
              1. Information We Collect
            </h2>
            <p>When you use Practap, we may temporarily process:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Your uploaded CV</li>
              <li>Your voice note responses</li>
              <li>Basic technical information (browser, device type, etc.) for operational purposes.</li>
            </ul>
          </section>

          <section aria-labelledby="how-we-use-info">
            <h2 id="how-we-use-info" className="text-2xl font-semibold text-slate-100 mb-3">
              2. How We Use Your Information
            </h2>
            <p>We use your input data only during your active session to:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
              <li>Generate personalized mock interview questions</li>
              <li>Analyze your responses and provide feedback</li>
            </ul>
            <p className="mt-2">
              Crucially, Practap is designed to be privacy-centric. <strong>We do not store, retain, or share your CV, voice recordings, or detailed session interaction data after your session ends and the browser tab is closed.</strong> Any processing of this sensitive data is ephemeral and solely for the purpose of providing you with real-time service during your active use.
            </p>
          </section>

          <section aria-labelledby="data-security">
            <h2 id="data-security" className="text-2xl font-semibold text-slate-100 mb-3">
              3. Data Security
            </h2>
            <p>
              Although we do not store your sensitive personal data (CV, voice recordings) long-term, we employ industry-standard security practices for the data while it is being processed during your active session. This includes secure transmission (HTTPS) and measures to protect data in-transit. This data is then discarded after your session.
            </p>
          </section>

          <section aria-labelledby="third-parties">
            <h2 id="third-parties" className="text-2xl font-semibold text-slate-100 mb-3">
              4. Third Parties
            </h2>
            <p>We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties. This does not include trusted third parties who assist us in operating our website or servicing you (e.g., for hosting, analytics that do not track PII from sessions), so long as those parties agree to keep this information confidential or handle anonymized/aggregated data.
            </p>
          </section>

          <section aria-labelledby="your-rights">
            <h2 id="your-rights" className="text-2xl font-semibold text-slate-100 mb-3">
              5. Your Rights
            </h2>
            <p>
              Given our ephemeral data handling for sensitive inputs like CVs and voice notes, there is no long-term personal information of this nature stored by us to access, correct, or delete. If you have an account with us, you may have rights regarding your account information (e.g., email, name). For any questions or concerns about your privacy or to inquire about your account data, please contact us at:{" "}
              <a
                href={`mailto:${privacyContactEmail}`}
                className="text-sky-400 hover:text-sky-300" /* Contrast Fix */
              >
                {privacyContactEmail}
              </a>
              .
            </p>
          </section>

          <section aria-labelledby="policy-changes">
            <h2 id="policy-changes" className="text-2xl font-semibold text-slate-100 mb-3">
              6. Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

        </div>
        {/* ===================================================================================== */}
        {/* END ACTUAL PRIVACY POLICY CONTENT                                                     */}
        {/* ===================================================================================== */}


        <div className="mt-10 pt-6 border-t border-slate-700 text-center"> {/* Added border top for separation */}
          <p className="text-slate-400 text-sm"> {/* Contrast Fix */}
            Practap - Master your communication. Ace your interviews.
          </p>
        </div>
      </article>
    </main>
  );
};

export default PrivacyPolicyPage;
